'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { Icon } from '@vinted/web-ui'
import { ArrowLeft24 } from '@vinted/monochrome-icons'
import { v4 as uuid } from 'uuid'

import { useTracking } from '@marketplace-web/shared/event-tracker'
import { useTranslate } from '@marketplace-web/shared/i18n'
import { CatalogAttribute } from 'constants/catalog'
import { FacetedCategoryDto } from 'types/dtos/catalog'
import { Screen } from 'constants/tracking/screens'
import { selectCatalogEvent } from '_libs/common/event-tracker/events'
import { useFacetedCategoriesContext } from 'pages/Catalog/FacetedCategoriesProvider'
import { useAbTest, useTrackAbTestCallback } from '@marketplace-web/shared/ab-tests'

import FilterCell from '../../common/FilterCell'
import FilterModal from '../../common/FilterModal'
import FilterModalNavigation from '../../common/FilterModalNavigation'

import CatalogFilterFacetedBase from './CatalogFilterFacetedBase'
import useNavStack from './useNavStack'
import { findCategory, generateNavStack } from './utils'

type Props = {
  catalogIds: Array<number>
  onCatalogSelect: (ids: Array<number>) => void
  onFilterCellClick: () => void
  onFilterCellView: () => void
  onModalOpen: () => void
  onModalClose: () => void
}

const CatalogFilterFacetedModal = ({
  catalogIds,
  onCatalogSelect,
  onModalClose,
  onModalOpen,
  onFilterCellView,
  onFilterCellClick,
}: Props) => {
  const translate = useTranslate('catalog.filters')
  const { categories } = useFacetedCategoriesContext()
  const { track } = useTracking()

  const holdoutQ4 = useAbTest('buyer_domain_holdout_2024q4')?.variant !== 'off'
  const facetedCategoriesAb = useAbTest('scoped_search_faceted_categories_v2')
  const trackAbTest = useTrackAbTestCallback()

  const [navStack, setNavStack] = useNavStack(categories, catalogIds)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const selectedCategory = useMemo(() => {
    if (!categories) return undefined

    const targetId = catalogIds[0]
    if (!targetId) return undefined

    return findCategory(categories, targetId)
  }, [catalogIds, categories])

  const selectedNavStack = useMemo(() => {
    if (!categories) return undefined

    const targetId = catalogIds[0]
    if (!targetId) return undefined

    return generateNavStack(categories, targetId)
  }, [catalogIds, categories])

  const seenItems = useRef(new Set<string>())
  const filteringSessionId = useRef(uuid())

  useEffect(() => {
    seenItems.current.clear()
  }, [navStack])

  const handleOpenModal = () => {
    if (holdoutQ4) trackAbTest(facetedCategoriesAb)

    if (catalogIds.length === 0) {
      setNavStack([])
    }

    setIsModalOpen(true)
    onModalOpen()
    onFilterCellClick()
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    onModalClose()

    filteringSessionId.current = uuid()
    seenItems.current.clear()
  }

  const trackCatalogSelection = (ids: Array<number>) => {
    track(
      selectCatalogEvent({
        catalogIds: ids,
        attributeId: CatalogAttribute.Filter,
        screen: Screen.MobileCatalogFilter,
      }),
    )
  }

  const handleClickLeaf = (category: FacetedCategoryDto) => {
    const categoryId = Number(category.id)

    let newSelectedIds: Array<number> = []

    if (catalogIds.includes(categoryId)) {
      newSelectedIds = catalogIds.filter(cat => cat !== categoryId)
    } else {
      newSelectedIds = [categoryId]
    }

    onCatalogSelect(newSelectedIds)
    trackCatalogSelection(newSelectedIds)

    handleCloseModal()
  }

  const renderRootNavigation = () => {
    return (
      <FilterModalNavigation
        title={translate('catalog.name')}
        icon={<Icon name={ArrowLeft24} testId="category-navigation-prefix-icon" />}
        prefixAriaLabel={translate('a11y.actions.back')}
        onPrefixClick={handleCloseModal}
      />
    )
  }

  const renderNavigation = (category: FacetedCategoryDto, back: () => void) => {
    return (
      <FilterModalNavigation
        title={category.title}
        icon={<Icon name={ArrowLeft24} testId="category-navigation-prefix-icon" />}
        prefixAriaLabel={translate('a11y.actions.back')}
        onPrefixClick={back}
      />
    )
  }

  const renderSuffixText = (category?: FacetedCategoryDto) => {
    // show title on Category filter cell
    if (!category) return selectedCategory?.title

    if (category.categories.length === 0) return undefined

    if (!selectedNavStack) return undefined
    if (!selectedNavStack.includes(Number(category.id))) return undefined

    return selectedCategory?.title
  }

  return (
    <>
      <FilterCell
        title={translate('catalog.name')}
        onClick={handleOpenModal}
        suffixText={renderSuffixText() || translate('catalog.all')}
        suffixTextTheme={renderSuffixText() ? 'primary' : undefined}
        onView={onFilterCellView}
      />

      <FilterModal isOpen={isModalOpen} hideSubmitButton>
        <CatalogFilterFacetedBase
          seenItems={seenItems}
          filteringSessionId={filteringSessionId}
          fromScreen={Screen.Catalog}
          onClickLeaf={handleClickLeaf}
          renderSuffixText={renderSuffixText}
          renderRootNavigation={renderRootNavigation}
          renderNavigation={renderNavigation}
          selectedIds={catalogIds}
          categories={categories || []}
          navStack={navStack}
          setNavStack={setNavStack}
        />
      </FilterModal>
    </>
  )
}

export default CatalogFilterFacetedModal
